import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, StickyNote, Eye, Edit2, Trash2, Pin, Archive, MoreVertical, Paperclip, Tag } from 'lucide-react';
import {
  useNotes,
  useDeleteNote,
  useTogglePinNote,
  useArchiveNote,
  getCategoryLabel,
  getCategoryColor,
  getPriorityLabel,
  getPriorityColor,
  stripHtml,
  getAllTags,
  type Note,
  type NoteCategory,
  type NotePriority
} from '../../../services/queries/noteQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { CreateNoteModal } from './CreateNoteModal';
import { EditNoteModal } from './EditNoteModal';
import { ViewNoteModal } from './ViewNoteModal';
import { useRecentItems } from '../../../hooks/useRecentItems';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

export const NotesHome: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<NoteCategory | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<NotePriority | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [noteMenuOpen, setNoteMenuOpen] = useState<string | null>(null);

  const { data: notes, isLoading, error } = useNotes({
    searchQuery,
    category: categoryFilter,
    priority: priorityFilter,
    tags: selectedTags,
    isArchived: showArchived
  });

  const deleteNoteMutation = useDeleteNote();
  const togglePinMutation = useTogglePinNote();
  const archiveNoteMutation = useArchiveNote();
  const { addRecentItem } = useRecentItems();
  const { success, error: showError } = useToast();

  // Track page view in Recent Items
  useEffect(() => {
    addRecentItem({
      id: 'notes-management',
      title: 'Notes Management',
      subtitle: 'Manage system notes and templates',
      path: '/admin?section=notes',
      type: 'page',
      icon: 'FileText'
    });
  }, [addRecentItem]);

  const allTags = getAllTags();
  const totalNotes = notes?.length || 0;
  const pinnedNotes = notes?.filter(n => n.isPinned).length || 0;
  const notesWithAttachments = notes?.filter(n => n.attachments.length > 0).length || 0;

  const handleDelete = async (noteId: string, noteTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${noteTitle}"?`)) return;

    try {
      await deleteNoteMutation.mutateAsync(noteId);
      success('Note deleted successfully!');
      setNoteMenuOpen(null);
    } catch (err: any) {
      showError('Failed to delete note', err.message || 'Please try again');
    }
  };

  const handleTogglePin = async (noteId: string) => {
    try {
      await togglePinMutation.mutateAsync(noteId);
      setNoteMenuOpen(null);
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleArchive = async (noteId: string, archive: boolean) => {
    try {
      await archiveNoteMutation.mutateAsync({ noteId, archive });
      setNoteMenuOpen(null);
    } catch (error) {
      console.error('Failed to archive note:', error);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notes Management</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Create and organize notes with rich text and attachments
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Note
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={notes ? `${notes.length} notes` : 'notes'}
          customMessage={`Development Mode: ${notes ? `Showing ${notes.length} notes` : 'Using mock data'} persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalNotes}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Notes</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <StickyNote className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {pinnedNotes}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Pinned Notes</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Pin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {notesWithAttachments}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">With Attachments</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Paperclip className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <StandardSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search notes by title, content, or tags..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />

              <StandardSelect
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as NoteCategory | 'all')}
                options={[
                  { value: 'all', label: 'All Categories' },
                  { value: 'general', label: 'General' },
                  { value: 'meeting', label: 'Meeting' },
                  { value: 'task', label: 'Task' },
                  { value: 'idea', label: 'Idea' },
                  { value: 'issue', label: 'Issue' },
                  { value: 'decision', label: 'Decision' }
                ]}
              />

              <StandardSelect
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as NotePriority | 'all')}
                options={[
                  { value: 'all', label: 'All Priorities' },
                  { value: 'high', label: 'High Priority' },
                  { value: 'medium', label: 'Medium Priority' },
                  { value: 'low', label: 'Low Priority' }
                ]}
              />

              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <Archive className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Archived</span>
              </label>
            </div>
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Filter by tags:</span>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    selectedTags.includes(tag)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
                >
                  Clear tags
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading notes..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading notes"
          message="Please try again."
        />
      )}

      {/* Notes Grid */}
      {!isLoading && !error && (
        <>
          {notes && notes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onView={() => setViewingNote(note)}
                  onEdit={() => setEditingNote(note)}
                  onDelete={() => handleDelete(note.id, note.title)}
                  onTogglePin={() => handleTogglePin(note.id)}
                  onArchive={(archive) => handleArchive(note.id, archive)}
                  menuOpen={noteMenuOpen === note.id}
                  onMenuToggle={() => setNoteMenuOpen(noteMenuOpen === note.id ? null : note.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<StickyNote className="w-16 h-16" />}
              title={searchQuery || categoryFilter !== 'all' || priorityFilter !== 'all' || selectedTags.length > 0 || showArchived ? 'No notes found' : 'No notes yet'}
              message={searchQuery || categoryFilter !== 'all' || priorityFilter !== 'all' || selectedTags.length > 0 || showArchived
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first note to get started'
              }
              actionLabel={searchQuery || categoryFilter !== 'all' || priorityFilter !== 'all' || selectedTags.length > 0 || showArchived ? 'Clear all filters' : undefined}
              onAction={searchQuery || categoryFilter !== 'all' || priorityFilter !== 'all' || selectedTags.length > 0 || showArchived
                ? () => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setPriorityFilter('all');
                    setSelectedTags([]);
                    setShowArchived(false);
                  }
                : undefined
              }
            />
          )}
        </>
      )}

      {/* Modals */}
      <CreateNoteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <EditNoteModal
        note={editingNote}
        isOpen={!!editingNote}
        onClose={() => setEditingNote(null)}
      />

      <ViewNoteModal
        note={viewingNote}
        isOpen={!!viewingNote}
        onClose={() => setViewingNote(null)}
        onEdit={() => {
          setEditingNote(viewingNote);
          setViewingNote(null);
        }}
      />
    </div>
  );
};

// Note Card Component
interface NoteCardProps {
  note: Note;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePin: () => void;
  onArchive: (archive: boolean) => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const NoteCard: React.FC<NoteCardProps> = ({
  note,
  onView,
  onEdit,
  onDelete,
  onTogglePin,
  onArchive,
  menuOpen,
  onMenuToggle
}) => {
  const contentPreview = stripHtml(note.content).substring(0, 150);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border-2 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer ${
      note.isPinned
        ? 'border-purple-300 dark:border-purple-700'
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700" onClick={onView}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {note.isPinned && (
                <Pin className="w-4 h-4 text-purple-600 dark:text-purple-400 fill-current" />
              )}
              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                {note.title}
              </h3>
            </div>
          </div>

          <div className="relative ml-2" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onMenuToggle}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={onMenuToggle}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    <button
                      onClick={onView}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Full Note
                    </button>
                    <button
                      onClick={onEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={onTogglePin}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Pin className="w-4 h-4" />
                      {note.isPinned ? 'Unpin' : 'Pin'} Note
                    </button>
                    <button
                      onClick={() => onArchive(!note.isArchived)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Archive className="w-4 h-4" />
                      {note.isArchived ? 'Unarchive' : 'Archive'}
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                    <button
                      onClick={onDelete}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(note.category)}`}>
            {getCategoryLabel(note.category)}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(note.priority)}`}>
            {getPriorityLabel(note.priority)}
          </span>
          {note.isArchived && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium">
              Archived
            </span>
          )}
        </div>
      </div>

      {/* Body Preview */}
      <div className="p-4" onClick={onView}>
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-3">
          {contentPreview}...
        </p>

        {/* Tags */}
        {note.tags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap mb-3">
            {note.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {note.tags.length > 3 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                +{note.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div>
            {note.attachments.length > 0 && (
              <div className="flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                <span>{note.attachments.length} attachment{note.attachments.length > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
          <div>
            {new Date(note.lastModified).toLocaleDateString()}
          </div>
        </div>
      </div>
    </div>
  );
};

